import { shallowCopy, Solid, Linear, ColorType } from "@noli/core";
import {
  CustomColor,
  GradientColor,
  SolidColor,
} from "@noli/noli-ui/dist/components/ColorPicker/typed";

type D2coordinates = [number, number];
const angle2Radian = (angle: number) => (angle * Math.PI) / 180;
const radian2Angle = (radian: number) => (radian * 180) / Math.PI;
const compressTo180 = (angle: number) => {
  const restA = angle % 360;
  if (restA > 180) return restA - 360;
  if (restA < -180) return restA + 360;
  return restA;
};
const coord2Angle = (coord: [D2coordinates, D2coordinates]) => {
  const [x1, y1] = coord[0];
  const [x2, y2] = coord[1];

  const dx = x2 - x1;
  const dy = y2 - y1;

  const radia = Math.atan2(dy, dx);
  return radian2Angle(radia);
};
const angle2Coord = (originalCoord: [D2coordinates, D2coordinates], angle: number) => {
  angle = compressTo180(angle);

  let x1 = 0;
  let y1 = 0;
  let x2;
  let y2;
  const radian = angle2Radian(angle);

  if (angle === 0) {
    x2 = 1;
    y2 = 0;
  }
  if (angle === 180 || angle === -180) {
    x2 = -1;
    y2 = 0;
  }
  if (angle === -90) {
    x2 = 0;
    y2 = -1;
  }
  if (angle === 90) {
    x2 = 0;
    y2 = 1;
  }

  y2 = Math.sin(radian);
  x2 = Math.cos(radian);

  // 坐标系转换(半径中心移动到元点)
  x1 -= x2 / 2;
  y1 -= y2 / 2;
  x2 /= 2;
  y2 /= 2;

  const [ox1, oy1] = originalCoord[0];
  const [ox2, oy2] = originalCoord[1];
  // 原中心点
  const ocX = ox1 + (ox2 - ox1) / 2;
  const ocY = oy1 + (oy2 - oy1) / 2;
  // 原直径
  const radius = Math.sqrt((oy2 - oy1) ** 2 + (ox2 - ox1) ** 2);
  const ratio = radius / 1;

  // 按比例拉伸
  x1 *= ratio;
  y1 *= ratio;
  x2 *= ratio;
  y2 *= ratio;

  // 坐标系转换(半径中心移动到 原中心点)
  x1 += ocX;
  x2 += ocX;
  y1 += ocY;
  y2 += ocY;

  return [
    [x1, y1],
    [x2, y2],
  ];
};
export const uiColorToSvgColor = (color: CustomColor, originColorChannel: ColorType): ColorType => {
  const svgColor =
    color.type === "SOLID"
      ? {
          color: color.color,
          type: color.type,
          opacity: color.opacity,
        }
      : {
          gradientStops: color.gradientStops.map((item) => {
            const newItem = {
              color: item.color,
              opacity: item.opacity,
              offset: item.position,
            };
            return newItem;
          }),
          type: color.type,
        };
  if (color.type === "LINEAR") {
    const {
      x1: originX1 = 0,
      x2: originX2 = 1,
      y1: originY1 = 0,
      y2: originY2 = 0,
    } = originColorChannel as Linear;

    const newCoord = angle2Coord(
      [
        [Number(originX1), Number(originY1)],
        [Number(originX2), Number(originY2)],
      ],
      color.gradientAngle as number,
    );

    const [coord1, coord2] = newCoord;
    const [x1, y1] = coord1;
    const [x2, y2] = coord2;

    Object.assign(svgColor, { x1, y1, x2, y2 });
  }
  return svgColor as ColorType;
};
export const svgColorToUiColor = (color: ColorType): CustomColor => {
  const uiColor =
    color.type === "SOLID"
      ? ({
          color: (color as Solid).color,
          opacity: Number((color as Solid).opacity),
          type: "SOLID",
        } as SolidColor)
      : ({
          type: color.type,
          gradientStops: (color as Linear).gradientStops.map((item) => ({
            ...shallowCopy(item),
            position: item.offset,
          })),
          gradientAngle: coord2Angle([
            [Number((color as Linear).x1), Number((color as Linear).y1)],
            [Number((color as Linear).x2), Number((color as Linear).y2)],
          ]),
        } as GradientColor);

  return uiColor;
};
